/** The stat keys the battle kernel mints, in one place.
 *
 * `StatKey` is the shared vocabulary of things content can name. A good part of
 * what the battle kernel puts on a stack is not content-nameable at all - a
 * damage bracket, a school's mastery, the share of a blow a Ember Mantle throws
 * back - so those keys are minted here with a cast and never appear in a data
 * file.
 *
 * They live in their own module rather than beside the rule that reads them
 * because several rules read the same key from opposite ends: `abilities.ts`
 * pays out a Ember Mantle that `spells.ts` put on, and `damage.ts` reads a
 * Slayer bonus the same spell set. A constant in either file makes the two
 * import each other, and a cycle in ESM is not a lint warning - it is a
 * `ReferenceError` the first time a battle starts.
 */

import type { StatKey } from "../modifiers.js";
import type { SpellSchool } from "../content/registry.js";

// ---------------------------------------------------------------- damage

export const STAT_DAMAGE_MELEE = "damage.melee" as StatKey;
export const STAT_DAMAGE_RANGED = "damage.ranged" as StatKey;
/** Additive to the bonus bracket regardless of attack kind. */
export const STAT_DAMAGE_DEALT = "damage.dealt" as StatKey;
/** Each modifier with this key is its own `(1 - value/FP_ONE)` reduction factor. */
export const STAT_DAMAGE_TAKEN = "damage.taken" as StatKey;
/** Fixed-point share of the distance penalty a shooter's kit cancels. */
export const STAT_RANGE_PENALTY_CUT = "damage.range_cut" as StatKey;

// ----------------------------------------------------------------- magic

/** Hero-side profile, installed by `heroMagicModifiers`. */
export const STAT_WISDOM = "magic.wisdom" as StatKey;
export const MASTERY_STAT: Readonly<Record<SpellSchool, StatKey>> = Object.freeze({
  air: "magic.mastery.air" as StatKey,
  earth: "magic.mastery.earth" as StatKey,
  fire: "magic.mastery.fire" as StatKey,
  water: "magic.mastery.water" as StatKey,
  all: "magic.mastery.all" as StatKey,
});

/** Target-side. Each is a fixed-point share of spell damage removed. */
export const RESIST_STAT: Readonly<Record<SpellSchool, StatKey>> = Object.freeze({
  air: "magic.resist.air" as StatKey,
  earth: "magic.resist.earth" as StatKey,
  fire: "magic.resist.fire" as StatKey,
  water: "magic.resist.water" as StatKey,
  all: "magic.resist.all" as StatKey,
});

/**
 * Attacker-side, the mirror of `RESIST_STAT`: a fixed-point share ADDED to the
 * stack's blows because they carry that element. A charged blow (`MAGIC_ATTACK`)
 * is one source; an enchanted-weapon spell is the other, and because this is a
 * stat rather than an ability, an artifact or a skill could grant it too.
 *
 * The target's `RESIST_STAT` of the same school blunts it, which is the whole
 * point of naming a school at all.
 */
export const STRIKE_STAT: Readonly<Record<SpellSchool, StatKey>> = Object.freeze({
  air: "magic.strike.air" as StatKey,
  earth: "magic.strike.earth" as StatKey,
  fire: "magic.strike.fire" as StatKey,
  water: "magic.strike.water" as StatKey,
  all: "magic.strike.all" as StatKey,
});

/** Null Ward: the highest spell level the stack is sealed against. */
export const STAT_ANTI_MAGIC = "magic.antimagic" as StatKey;

/**
 * Sundering Orb: non-zero and no creature's own immunity holds.
 *
 * Field-scoped rather than army-scoped, because the artifact says "no creature
 * on the field" - it strips the bearer's own dragons as readily as the enemy's.
 * That is the artifact's cost and the reason it is not simply an upgrade.
 */
export const STAT_IGNORE_IMMUNITY = "magic.ignore_immunity" as StatKey;

/**
 * Band of Forgetting: non-zero and nothing that dies here comes back.
 *
 * Field-scoped and deliberately two-ended: it stops Resurrection and Animate
 * Dead during the battle, and it stops Gravecalling raising the fallen after it.
 * Either hero wearing it binds both, which is what "by either side" means.
 */
export const STAT_NO_RAISE = "battle.no_raise" as StatKey;

/**
 * A hero's specialty in one spell, as a fixed-point multiplier on that spell's
 * damage and healing. Minted per spell id, so the key names the spell and no
 * rule has to.
 */
export const spellSpecialtyStat = (spell: string): StatKey =>
  `magic.specialty.${spell}` as StatKey;

/** `schoolDamage.<school>` as it travels: one school's own damage multiplier. */
export const schoolDamageStat = (school: string): StatKey =>
  `magic.school.${school}` as StatKey;

/** `immune.<spellId>`, as it travels on a stack. */
export const spellImmunityStat = (spell: string): StatKey =>
  `magic.immune.${spell}` as StatKey;

/**
 * Dusk Seal: one school the hero on the OTHER side may not cast from.
 *
 * Written into the wearer's own field modifiers and read by their opponent,
 * which is the same direction `spellPowerEnemy` points. That is what makes it
 * one-sided: the level cap and floor are read from the caster's own mods and
 * bind whoever asks, this one is read across the field and binds only the foe.
 */
export const sealedSchoolStat = (school: string): StatKey =>
  `magic.sealed.${school}` as StatKey;

/** Percent of hostile casts an army shrugs off on top of any ability. */
export const STAT_MAGIC_RESIST = "magic.resist_pct" as StatKey;

/**
 * Field-wide gates, installed on every stack on both sides by `startBattle` and
 * therefore visible through the side's magic modifiers whichever hero asks. A
 * Oathbreaker's Cloak that silenced only the other man would be an artifact you
 * wear to lose.
 */
export const STAT_SPELL_LEVEL_CAP = "magic.level_cap" as StatKey;
export const STAT_SPELL_LEVEL_FLOOR = "magic.level_floor" as StatKey;
export const STAT_NO_ESCAPE = "magic.no_escape" as StatKey;

// ------------------------------------------------- statuses a spell leaves

/** Riposte: retaliations granted on top of the creature's own. */
export const STAT_EXTRA_RETALIATION = "battle.extra_retaliation" as StatKey;
/** Ember Mantle: the percent of a melee blow thrown back at whoever struck. */
export const STAT_FIRE_SHIELD = "battle.ember_mantle" as StatKey;
/** Spell Mirror: basis points of rebounding a hostile spell. */
export const STAT_MAGIC_MIRROR = "battle.spell_mirror" as StatKey;
/** Slayer: the attack it adds, and the lowest tier it counts as quarry. */
export const STAT_SLAYER_ATTACK = "battle.slayer_attack" as StatKey;
export const STAT_SLAYER_TIER = "battle.slayer_tier" as StatKey;
/**
 * Regeneration: hit points mended at the start of each of the stack's turns.
 *
 * A number on the stack rather than a countdown of its own, so the spell's
 * duration is the modifier's duration and `expireRoundMods` is the only clock -
 * the same arrangement Clone and Enthrall use, for the same reason.
 */
export const STAT_REGEN = "battle.regen" as StatKey;
/**
 * Vampirism: basis points of the damage a blow deals that its own stack heals.
 *
 * The creature ability drains in full and this is a share, so the two are read
 * at the same place and the ability is simply the case where the share is
 * ten thousand. Capped where it is read, not where it is written.
 */
export const STAT_LIFE_DRAIN = "battle.life_drain" as StatKey;
/** Enthrall and Clone, as statuses whose lapse the round tick reads. */
export const STAT_HYPNOSIS = "battle.hypnosis" as StatKey;
export const STAT_CLONE = "battle.clone" as StatKey;
/**
 * Madness stands while this does. `F_BERSERK` says the stack is out of its
 * mind and this says for how long - the same two-part shape `F_CLONE` and
 * `F_HYPNOTIZED` use, and for the same reason: the flag is what the rules read
 * every turn, and the duration is a modifier so that it expires through the one
 * mechanism every other duration expires through.
 *
 * Without it Blood Rage was permanent, which nobody noticed because it also did
 * nothing at all.
 */
export const STAT_BERSERK = "battle.blood_rage" as StatKey;
/**
 * Blind, the spell: value is the caster's mastery plus one, so 1..4. While it
 * stands the stack keeps losing its turn; the blow that wakes it is halved;
 * at expert (4) the waking blow draws no retaliation. A blind with no
 * modifier behind it is an ability's - one lost turn, as before.
 */
export const STAT_BLIND = "battle.blind" as StatKey;

// ------------------------------------------------------- war-machine crew

/** The hero's Runes level, installed army-wide. See `battle/runes.ts`. */
export const STAT_RUNES = "battle.runes" as StatKey;

/**
 * Extra rune levels a stack may bank, over what its hero's Runes level allows.
 *
 * Separate from `STAT_RUNES` because the two answer different questions: that
 * one is how well the hero was taught, which decides both the cap *and* which
 * levels the hero pays for; this one only widens the ceiling.
 */
export const STAT_RUNE_CAP = "battle.rune_cap" as StatKey;

export const STAT_CREW_ARTILLERY = "machine.artillery" as StatKey;
export const STAT_CREW_BALLISTICS = "machine.ballistics" as StatKey;
export const STAT_CREW_FIRST_AID = "machine.first_aid" as StatKey;
